/**
 * Hidden-endpoint access for the relay bot dashboard.
 *
 * There is no login. The dashboard is unlocked by opening the app once with a secret
 * in the URL — `https://newall.network/?mirror=<KEY>` — which is captured here, kept in
 * this browser's localStorage, and stripped back out of the address bar so it doesn't
 * linger in history or a referrer. From then on the key rides on every monitoring
 * request (see `api.ts`) and the "Bot Cermin" menu entry appears; a browser that never
 * saw the link sees neither the entry nor any data.
 *
 * This is obscurity, not real authentication: anyone with the link has full access, so
 * the link is the secret — treat it like a password.
 */

import pause from '@helpers/schedulers/pause';

const STORAGE_KEY = 'mirror_access_key';
const URL_PARAM = 'mirror';

// Monitor mode: a browser that opened `?monitor=1` becomes a dedicated dashboard
// front-end — it shows the username/password gate instead of the normal Telegram
// login, and drops straight into the relay-bot dashboard once in. The flag is sticky
// so every later refresh does the same, without the `?monitor=1` in the URL.
const MODE_KEY = 'mirror_monitor_mode';
const MODE_PARAM = 'monitor';

// `?monitor=0` (or `off`/`false`/`no`) leaves dashboard mode again. The kiosk hides the
// app's own chrome, so the way out has to be reachable from the URL alone.
const MODE_OFF_VALUES = new Set(['0', 'off', 'false', 'no']);

/**
 * If the current URL carries the secret, store it and scrub it from the address bar.
 * Also detect /monitor path to enable monitor mode.
 * Safe to call more than once and safe where storage is unavailable. Runs at startup
 * (before the burger menu is built) so an opened link unlocks the entry immediately.
 */
export function captureMonitorKey() {
  try {
    // Auto-enable monitor mode if accessing /monitor path (secret route)
    if(location.pathname === '/monitor' || location.pathname.startsWith('/monitor/')) {
      localStorage.setItem(MODE_KEY, '1');
      // Ensure URL is clean — /monitor is all that's needed, no query params
      if(location.search || location.hash) {
        history.replaceState(history.state, '', '/monitor');
      }
    }

    const search = new URLSearchParams(location.search);
    // Also accept it inside the hash (`#mirror=…` or `#…&mirror=…`) since tweb itself
    // routes on the hash and a shared link might land there.
    const hash = new URLSearchParams(location.hash.replace(/^#/, ''));

    const key = search.get(URL_PARAM) || hash.get(URL_PARAM);
    if(key) localStorage.setItem(STORAGE_KEY, key);

    // `?monitor=1` turns this browser into the password-gated dashboard front-end,
    // `?monitor=0` turns it back into the normal app. The access key is left alone
    // either way — that one is what unlocks the hidden menu entry in a normal session.
    const mode = search.get(MODE_PARAM) ?? hash.get(MODE_PARAM);
    if(mode !== null) {
      if(MODE_OFF_VALUES.has(mode)) localStorage.removeItem(MODE_KEY);
      else localStorage.setItem(MODE_KEY, '1');
    }

    if(!key && mode === null) return;

    // Rebuild the URL without the secret, leaving everything else (peer hash, other
    // query params) exactly as it was.
    search.delete(URL_PARAM);
    hash.delete(URL_PARAM);
    search.delete(MODE_PARAM);
    hash.delete(MODE_PARAM);
    const newSearch = search.toString();
    const newHash = hash.toString();
    const url = location.pathname +
      (newSearch ? '?' + newSearch : '') +
      (newHash ? '#' + newHash : '');
    history.replaceState(history.state, '', url);
  } catch(err) {}
}

/**
 * Ping the endpoint to see whether the stored key still works, and forget it if it
 * doesn't (a 401 — the key was rotated on the server, or never valid). This is what
 * makes the hidden menu entry vanish again on a browser whose key has gone stale,
 * without waiting for the user to open the tab. A network error is left alone: a
 * transient outage must not lock someone out of their own key.
 */
export function validateMonitorKey(): Promise<void> {
  // One check per page load, shared: the boot path fires it early and forgets it, while
  // monitor mode awaits the very same promise before deciding whether to show its sign-in
  // gate — without that the gate would race the check and a rotated key would land in a
  // dashboard that can only say "locked".
  return validation ??= (async() => {
    const key = getMonitorKey();
    if(!key) return;

    try {
      const response = await fetch('/api/monitor/me', {
        credentials: 'same-origin',
        headers: {'X-Monitor-Key': key}
      });
      if(response.status === 401) clearMonitorKey();
    } catch(err) {}
  })();
}

let validation: Promise<void>;

/**
 * True once the stored key has been checked against the server and survived — i.e. this
 * browser can talk to the dashboard. Monitor mode uses it to choose between opening the
 * dashboard and asking for the password again.
 */
export async function hasValidMonitorKey(): Promise<boolean> {
  if(!hasMonitorKey()) return false;

  // The boot path waits on this, so a request that hangs would hold the whole app on a
  // blank page. Past a few seconds, do what the network-error branch does — keep the key
  // and carry on; the check itself finishes on its own time.
  await Promise.race([validateMonitorKey(), pause(3000)]);
  return hasMonitorKey();
}

export function getMonitorKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch(err) {
    return '';
  }
}

export function hasMonitorKey(): boolean {
  return !!getMonitorKey();
}

/** Store the access key handed back by the password gate (POST /session). */
export function setMonitorKey(key: string) {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch(err) {}
}

/** Forget the key — the dashboard locks again until the secret link is opened anew. */
export function clearMonitorKey() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch(err) {}
}

/** True in a browser that has entered dashboard (username/password) mode. */
export function isMonitorMode(): boolean {
  try {
    return localStorage.getItem(MODE_KEY) === '1';
  } catch(err) {
    return false;
  }
}

/** Leave dashboard mode (used by the sign-out path). */
export function clearMonitorMode() {
  try {
    localStorage.removeItem(MODE_KEY);
  } catch(err) {}
}
